import { useMemo, useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  UserRole,
  formatLoaderSerial,
  LOADER_REMOVAL_REASON_LABELS,
  LOADER_REMOVAL_REASON_CHOICES,
  type LoaderRemovalReason,
  type LoaderSummaryDto,
} from '@bhops/shared';
import { loadersApi, robotLoaderApi } from '@/api/loaders';
import { extractApiError } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { kstYmd } from '@/lib/format-date';
import { Button, Input } from '@/components/Form';
import { LoaderConfigCode } from '@/components/LoaderConfigCode';
import { Modal } from '@/components/Modal';
import { LoaderStatusPill } from '@/components/StatusPill';
import {
  Fact,
  FactList,
  HistoryList,
  Sheet,
  SheetEmpty,
  SheetHead,
  type HistoryItem,
} from './robot-detail-ui';

// docs/plan/loader-history-traceability-2026-07 — 로봇 상세 '로더' 탭.
//   상단: 현재 장착 로더 카드(디코드 배지 + 구성코드 + [교체]).
//   하단: 이 로봇의 로더 교체 이력 타임라인.
// 로더는 이 탭으로만 관리(일반 부품 그리드에서 LOADER 는 제외).

export function LoaderTab({ robotId }: { robotId: string }) {
  const role = useAuthStore((s) => s.user?.role);
  const canEdit =
    role === UserRole.ADMIN ||
    role === UserRole.MANAGER ||
    role === UserRole.MFG_MANAGER ||
    role === UserRole.FIELD_MANAGER;
  const [swapOpen, setSwapOpen] = useState(false);
  // 오너 요구(26-08-06) — 분리(이식). 서버 detach 는 처음부터 있었고 화면 입구만 없었다.
  const [detachOpen, setDetachOpen] = useState(false);

  const q = useQuery({
    queryKey: ['robot-loader', robotId],
    queryFn: () => robotLoaderApi.get(robotId),
    staleTime: 30_000,
  });

  // 한국시간 통일 5차(26-08-14) — 이력 날짜를 KST 로. 교체 모달이 날짜를 보내면
  // UTC 자정이지만, 안 보내면 서버가 그 순간을 찍는다(loaders.service:
  // `dto.installedAt ? new Date(dto.installedAt) : new Date()`) — 두 관례가 섞인
  // 컬럼이라 KST 로 읽어야 둘 다 운영자가 아는 날짜가 된다. 정렬 키로도 쓰지만
  // 표기가 점(`2026.08.14`)이어도 자리수가 같아 사전식 정렬 결과는 같다.
  const timelineEvents = useMemo<HistoryItem[]>(() => {
    const events: HistoryItem[] = [];
    for (const h of q.data?.history ?? []) {
      events.push({
        id: `install-${h.id}`,
        date: kstYmd(h.installedAt) ?? '-',
        color: 'var(--brand)',
        title: `${h.loaderSerial} 장착${h.direction ? `, ${h.direction}` : ''}`,
        tag: '장착',
        meta: h.note ?? undefined,
      });
      if (h.removedAt) {
        events.push({
          id: `remove-${h.id}`,
          date: kstYmd(h.removedAt) ?? '-',
          color: 'var(--fg-muted)',
          title: `${h.loaderSerial} 탈거`,
          tag: '탈거',
          meta: h.removalReason
            ? `사유: ${LOADER_REMOVAL_REASON_LABELS[h.removalReason]}`
            : '사유 미기록',
        });
      }
    }
    return events.sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));
  }, [q.data]);

  if (q.isLoading) return <div style={loadingStyle}>불러오는 중…</div>;
  if (q.error) return <div style={errorStyle}>{extractApiError(q.error)}</div>;

  const current = q.data?.current ?? null;

  return (
    <div className="rd-stack">
      {/* 2026-11-02 — 로봇 상세의 다른 탭과 같은 판(Sheet) 모양. */}
      <Sheet>
        <SheetHead
          title="현재 로더"
          actions={
            canEdit && (
              <>
                <Button
                  type="button"
                  onClick={() => setSwapOpen(true)}
                  data-testid="loader-swap-button"
                >
                  {current ? '로더 교체' : '로더 장착'}
                </Button>
                {/* 분리(이식) — 교체와 달리 새 로더를 붙이지 않고 떼어내기만 한다.
                  분리된 로더는 재고로 돌아가 다른 로봇의 [로더 장착]에서 바로 고를 수 있다. */}
                {current && (
                  <Button
                    type="button"
                    onClick={() => setDetachOpen(true)}
                    data-testid="loader-detach-button"
                  >
                    분리
                  </Button>
                )}
              </>
            )
          }
        />
        {current ? (
          <CurrentLoaderCard loader={current} />
        ) : (
          <SheetEmpty>
            {canEdit
              ? '장착된 로더가 없습니다. [로더 장착]으로 등록하세요.'
              : '장착된 로더가 없습니다.'}
          </SheetEmpty>
        )}
      </Sheet>

      <Sheet>
        <SheetHead title="로더 교체 이력" count={timelineEvents.length} />
        {timelineEvents.length === 0 ? (
          <SheetEmpty>이력이 없습니다.</SheetEmpty>
        ) : (
          <HistoryList items={timelineEvents} />
        )}
      </Sheet>

      <LoaderSwapModal
        open={swapOpen}
        onClose={() => setSwapOpen(false)}
        robotId={robotId}
        hasCurrent={current !== null}
      />
      <LoaderDetachModal
        open={detachOpen}
        onClose={() => setDetachOpen(false)}
        robotId={robotId}
        serial={current?.serial ?? null}
      />
    </div>
  );
}

// 오너 요구(26-08-06) — 로더 분리(이식). 서버 POST /robots/:id/loader/detach 는 사유 필수 ·
// 로더 재고(IN_STOCK) 복귀 · 감사 기록까지 처음부터 구현돼 있었고, 화면 입구만 없었다.
// 이식은 여기서 분리 → 다른 로봇의 기존 [로더 장착] 2단계로 끝난다(분리된 로더가 재고로
// 돌아가 장착 후보 목록에 바로 나타난다) — 원클릭 이식 흐름을 새로 만들지 않는다.
function LoaderDetachModal({
  open,
  onClose,
  robotId,
  serial,
}: {
  open: boolean;
  onClose: () => void;
  robotId: string;
  serial: string | null;
}) {
  const qc = useQueryClient();
  const [reason, setReason] = useState<LoaderRemovalReason | ''>('');

  const detachM = useMutation({
    mutationFn: () =>
      robotLoaderApi.detach(robotId, { removalReason: reason as LoaderRemovalReason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['robot-loader', robotId] });
      // 분리된 로더가 재고로 돌아가므로 장착 후보 목록(IN_STOCK)도 새로 읽어야 한다.
      qc.invalidateQueries({ queryKey: ['loaders'] });
      setReason('');
      onClose();
    },
  });

  return (
    <Modal open={open} onClose={onClose} title="로더 분리" width={440}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 13 }}>
          {serial ? (
            <>
              <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{serial}</span> 를
              이 로봇에서 분리합니다.
            </>
          ) : (
            '장착된 로더를 이 로봇에서 분리합니다.'
          )}
        </div>
        <label style={fieldLabel}>
          분리 사유 <span style={{ color: 'var(--fg-danger)' }}>*</span>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value as LoaderRemovalReason | '')}
            style={selectStyle}
            data-testid="loader-detach-reason"
          >
            <option value="">— 선택 —</option>
            {LOADER_REMOVAL_REASON_CHOICES.map((r) => (
              <option key={r} value={r}>
                {LOADER_REMOVAL_REASON_LABELS[r]}
              </option>
            ))}
          </select>
        </label>
        <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
          분리된 로더는 재고로 돌아가며, 다른 로봇의 [로더 장착]에서 바로 고를 수 있습니다. 다른
          로봇에 옮기는 작업이면 사유를 &lsquo;이식(다른 로봇 장착)&rsquo;으로 선택하세요.
        </div>
        {detachM.error ? (
          <div style={{ fontSize: 12, color: 'var(--fg-danger)' }}>
            {extractApiError(detachM.error)}
          </div>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button type="button" onClick={onClose}>
            취소
          </Button>
          <Button
            type="button"
            disabled={reason === '' || detachM.isPending}
            onClick={() => detachM.mutate()}
            data-testid="loader-detach-submit"
          >
            {detachM.isPending ? '분리 중…' : '분리'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function CurrentLoaderCard({ loader }: { loader: LoaderSummaryDto }) {
  return (
    <div className="rd-fact" style={{ paddingTop: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Link
          to={`/manufacturing/loaders/${loader.id}`}
          className="rd-version"
          style={{ color: 'var(--fg-brand)', textDecoration: 'none' }}
        >
          {loader.serial}
        </Link>
        <LoaderStatusPill status={loader.status} />
      </div>
      <FactList style={{ marginTop: 10 }}>
        <Fact k="구분" v={formatLoaderSerial(loader.serial)} />
        {/* 구성코드 — 올리면 코드별로 어떤 개조인지(로더 목록·로더 상세와 같은 부품). */}
        <Fact
          k="구성코드"
          v={loader.configCode ? <LoaderConfigCode code={loader.configCode} focusable /> : null}
        />
        <Fact k="업체" v={loader.vendor} />
      </FactList>
    </div>
  );
}

function LoaderSwapModal({
  open,
  onClose,
  robotId,
  hasCurrent,
}: {
  open: boolean;
  onClose: () => void;
  robotId: string;
  hasCurrent: boolean;
}) {
  const qc = useQueryClient();
  const [keyword, setKeyword] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [installedAt, setInstalledAt] = useState(todayYmd());
  const [direction, setDirection] = useState('');
  const [reason, setReason] = useState<LoaderRemovalReason | ''>('');
  const [note, setNote] = useState('');

  // 재고(IN_STOCK) 로더만 후보.
  const stockQ = useQuery({
    queryKey: ['loaders', 'stock'],
    queryFn: () => loadersApi.list({ status: 'IN_STOCK' }),
    enabled: open,
    staleTime: 30_000,
  });

  const swapM = useMutation({
    mutationFn: () =>
      robotLoaderApi.swap(robotId, {
        loaderUnitId: selected!,
        // UTC 자정으로 저장 — toYmd(UTC slice)와 round-trip(로컬자정은 KST 에서 하루 밀림).
        installedAt: installedAt ? `${installedAt}T00:00:00.000Z` : undefined,
        direction: direction.trim() || undefined,
        note: note.trim() || undefined,
        removalReason: hasCurrent ? (reason as LoaderRemovalReason) : undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['robot-loader', robotId] });
      qc.invalidateQueries({ queryKey: ['loaders'] });
      reset();
      onClose();
    },
  });

  function reset(): void {
    setKeyword('');
    setSelected(null);
    setReason('');
    setNote('');
    setDirection('');
  }

  const candidates = useMemo(() => {
    const items = stockQ.data?.items ?? [];
    const kw = keyword.trim().toLowerCase();
    return kw ? items.filter((l) => l.serial.toLowerCase().includes(kw)) : items;
  }, [stockQ.data, keyword]);

  const canSubmit = selected !== null && (!hasCurrent || reason !== '');

  return (
    <Modal open={open} onClose={onClose} title={hasCurrent ? '로더 교체' : '로더 장착'} width={560}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {hasCurrent && (
          <label style={fieldLabel}>
            기존 로더 탈거 사유 <span style={{ color: 'var(--fg-danger)' }}>*</span>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value as LoaderRemovalReason | '')}
              style={selectStyle}
              data-testid="loader-swap-reason"
            >
              <option value="">— 선택 —</option>
              {LOADER_REMOVAL_REASON_CHOICES.map((r) => (
                <option key={r} value={r}>
                  {LOADER_REMOVAL_REASON_LABELS[r]}
                </option>
              ))}
            </select>
          </label>
        )}

        <div>
          <div style={fieldLabel}>장착할 재고 로더</div>
          <Input
            placeholder="시리얼 검색 (예: G2H2607)"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            aria-label="로더 시리얼 검색"
            data-testid="loader-swap-search"
          />
          <div style={pickerList}>
            {stockQ.isLoading ? (
              <div style={emptyRowSm}>불러오는 중…</div>
            ) : candidates.length === 0 ? (
              <div style={emptyRowSm}>재고 로더가 없습니다.</div>
            ) : (
              candidates.slice(0, 50).map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => setSelected(l.id)}
                  style={{
                    ...pickerRow,
                    background: selected === l.id ? 'var(--brand-subtle)' : 'transparent',
                  }}
                  data-testid={`loader-pick-${l.id}`}
                >
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontWeight: selected === l.id ? 700 : 500,
                    }}
                  >
                    {l.serial}
                  </span>
                  <span style={{ color: 'var(--fg-muted)', fontSize: 12 }}>
                    {formatLoaderSerial(l.serial)}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <label style={{ ...fieldLabel, flex: 1 }}>
            장착일
            <Input
              type="date"
              value={installedAt}
              onChange={(e) => setInstalledAt(e.target.value)}
            />
          </label>
          <label style={{ ...fieldLabel, flex: 1 }}>
            방향 (선택)
            <Input
              placeholder="Left / Right"
              value={direction}
              onChange={(e) => setDirection(e.target.value)}
            />
          </label>
        </div>
        <label style={fieldLabel}>
          메모 (선택)
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </label>

        {swapM.isError && <div style={errorStyle}>{extractApiError(swapM.error)}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button
            type="button"
            onClick={() => swapM.mutate()}
            disabled={!canSubmit || swapM.isPending}
            data-testid="loader-swap-submit"
          >
            {swapM.isPending ? '처리 중…' : hasCurrent ? '교체' : '장착'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function todayYmd(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

const loadingStyle: CSSProperties = { padding: 40, textAlign: 'center', color: 'var(--fg-muted)' };
const errorStyle: CSSProperties = {
  padding: 16,
  color: 'var(--fg-danger)',
  background: 'var(--bg-danger-subtle)',
  borderRadius: 'var(--r-card)',
};
const fieldLabel: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 12,
  color: 'var(--fg-muted)',
};
const selectStyle: CSSProperties = {
  padding: '8px 10px',
  borderRadius: 'var(--r-btn)',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 14,
};
const pickerList: CSSProperties = {
  marginTop: 6,
  maxHeight: 200,
  overflow: 'auto',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
};
const pickerRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '8px 10px',
  width: '100%',
  textAlign: 'left',
  border: 'none',
  borderBottom: '1px solid var(--border-subtle)',
  cursor: 'pointer',
  color: 'var(--fg-default)',
};
const emptyRowSm: CSSProperties = {
  padding: 16,
  textAlign: 'center',
  fontSize: 12,
  color: 'var(--fg-muted)',
};
